import { AxiosError, AxiosRequestConfig } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';

import { API } from './api';
import useAPIClient from './useAPIClient';

export type InfiniteResponse<T> = {
  count?: number;
  next?: string;
  previous?: string;
  results: T[];
};

type QueryKey<URL extends API['url'], Method extends API['method'] = 'GET'> =
  | [
      URL,
      Extract<API, { url: URL; method?: Method; response: InfiniteResponse<unknown> }> extends {
        data?: infer D;
        params?: infer P;
      }
        ? Omit<AxiosRequestConfig<D>, 'params' | 'method'> & { method?: Method; params?: P }
        : never
    ]
  | [URL]
  | URL;

type QueryFnData<URL extends API['url'], Method extends API['method'] = 'GET'> = Extract<
  API,
  { url: URL; method?: Method; response: InfiniteResponse<unknown> }
> extends {
  response: InfiniteResponse<infer R>;
}
  ? InfiniteResponse<R>
  : never;

type UseInfiniteAPIQueryOptions<
  URL extends API['url'],
  Method extends API['method'] = 'GET',
  TData = QueryFnData<URL, Method>
> =
  | Omit<
      UseInfiniteQueryOptions<QueryFnData<URL, Method>, AxiosError, TData>,
      'queryKey' | 'queryFn'
    > & { pageKey?: string };

const useInfiniteAPIQuery = <
  URL extends API['url'],
  Method extends API['method'] = 'GET',
  TData = QueryFnData<URL, Method>
>(
  queryKey: QueryKey<URL, Method>,
  options: UseInfiniteAPIQueryOptions<URL, Method, TData> = {}
) => {
  const client = useAPIClient();

  const { pageKey = 'page', ...restOptions } = options;

  return useInfiniteQuery<QueryFnData<URL, Method>, AxiosError, TData>(
    [...(Array.isArray(queryKey) ? queryKey : [queryKey]), 'infinite'],
    async ({ queryKey, pageParam, signal }) => {
      const [route, requestConfig = {}] = queryKey as [string, AxiosRequestConfig];

      const { params, ...restRequestConfig } = requestConfig;

      const { data } = await client.request({
        url: `${route}`,
        method: 'GET',
        signal,
        ...(pageParam ? { params: { ...params, ...pageParam } } : { params }),
        ...restRequestConfig,
      });

      return data;
    },
    {
      getPreviousPageParam(lastPage) {
        if (lastPage.previous) {
          const url = new URL(lastPage.previous);
          return { [pageKey]: url.searchParams.get(pageKey) };
        }
        return undefined;
      },
      getNextPageParam(lastPage, allPages) {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return { [pageKey]: url.searchParams.get(pageKey) };
        }
        return undefined;
      },
      ...restOptions,
    }
  );
};

export default useInfiniteAPIQuery;
